import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { AppConfig } from "@src/config/AppConfig";
import { GetConfigDeductionSlaConfigsDTO } from "@src/domain/dto/config";
import type { ConfigDeductionSlaConfig } from "@src/domain/entities/config";
import { GetConfigDeductionSlaConfigsUseCase } from "@src/application/useCases/config";
import { ConfigDeductionSlaConfigRepository } from "@src/infrastructure/repositories/config";
import { useConfigListQuery } from "./useConfigListQuery";

interface ConfigDeductionSlaConfigsDataContextValue {
  brandKey: string;
  configs: ConfigDeductionSlaConfig[];
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  limit: number;
  total: number;
  setPage: (page: number) => void;
  refetch: () => void;
}

const ConfigDeductionSlaConfigsDataContext =
  createContext<ConfigDeductionSlaConfigsDataContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useConfigDeductionSlaConfigs(): ConfigDeductionSlaConfigsDataContextValue {
  const context = useContext(ConfigDeductionSlaConfigsDataContext);
  if (!context) {
    throw new Error(
      "useConfigDeductionSlaConfigs must be used within ConfigDeductionSlaConfigsDataProvider",
    );
  }
  return context;
}

interface ConfigDeductionSlaConfigsDataProviderProps {
  brandKey: string;
  children: ReactNode;
  defaultPageSize?: number;
}

export function ConfigDeductionSlaConfigsDataProvider({
  brandKey,
  children,
  defaultPageSize = AppConfig.getDefaultPaginationLimit(),
}: Readonly<ConfigDeductionSlaConfigsDataProviderProps>) {
  const getUseCase = useMemo(
    () =>
      new GetConfigDeductionSlaConfigsUseCase(
        new ConfigDeductionSlaConfigRepository(),
      ),
    [],
  );

  const fetchPage = useCallback(
    async (args: { search: string | null; page: number; limit: number }) =>
      getUseCase.execute(
        new GetConfigDeductionSlaConfigsDTO({
          search: args.search,
          page_number: args.page,
          page_size: args.limit,
        }),
      ),
    [getUseCase],
  );

  const {
    items: configs,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  } = useConfigListQuery({
    brandKey,
    defaultPageSize,
    errorMessage: "Failed to load SLA configs",
    cacheScope: "sla-config",
    fetchPage,
  });

  const value = useMemo(
    (): ConfigDeductionSlaConfigsDataContextValue => ({
      brandKey,
      configs,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    }),
    [
      brandKey,
      configs,
      loading,
      error,
      search,
      setSearch,
      page,
      limit,
      total,
      setPage,
      refetch,
    ],
  );

  return (
    <ConfigDeductionSlaConfigsDataContext.Provider value={value}>
      {children}
    </ConfigDeductionSlaConfigsDataContext.Provider>
  );
}
